/**
 * THE WORK AS A CONVERSATION (owner, 2026-10-16: "the heading can be static while the chat can be like … Claude";
 * "it's not something I have to scroll many sections").
 *
 * The middle of the work's desk: the work's details stay fixed at the top (what it is, the client and period, who
 * started it and who holds it, where it stands and when it resumes, the workpaper to download); below them only the
 * conversation scrolls — what the person said and sent on the right, what Leveri did and asked on the left, oldest
 * first — and the box to say the next thing stays at the foot. Anything a message names opens in the side panel; the
 * person never leaves the conversation. Every word is the server's: the work's shared record (`work.conversation`) and
 * the person's own messages here (private to them) are only set in time order.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { WorkActionResponse } from "@leveri/contracts";
import { call, failureMessage, save } from "../../api.js";
import { useKeyring } from "./keys.js";
import { useNavigate } from "react-router-dom";
import type { ChatHistoryResponse, ConversationEntry, WorkFile } from "@leveri/contracts";
import { Marker, SERIF_OPSZ, markerTone } from "../../file/index.js";
import { ChatBox, LeveriMark } from "../chat.js";
import { NeedsAttention } from "./attention.js";
import { StartSection } from "./start.js";
import { AssignControl } from "../assign.js";
import { OutsideReadingOffer } from "../documentReading.js";

export type ConversationLinkTarget = { kind: "REQUEST" | "DECISION" | "VIEW"; id: string };

type Turn = ChatHistoryResponse["turns"][number];
type Item = { at: string; key: string } & ({ entry: ConversationEntry } | { turn: Turn });

/**
 * WORDS TRUE ONLY WHILE THE WORK RUNS ("Leveri has the work in hand and is carrying it out…", "Leveri is preparing the
 * accounts again with it.") are dropped once it has settled — found on the live site, 2026-10-21, still on screen
 * under a finished draft. What the act itself did stays.
 */
const WHILE_RUNNING = /^(Leveri (has the work in hand|is (preparing|carrying|reading|working|reconciling))|It continues without this page open)/;
export function settledOutcome(outcome: string | null, working: boolean): string | null {
  if (!outcome || working) return outcome;
  const kept = outcome.split(/(?<=[.!?])\s+/).filter((s) => !WHILE_RUNNING.test(s.trim())).join(" ").trim();
  return kept || null;
}

export function WorkConversation({ work, outcome, onWork, onOutcome, onOpen, focus, onLists }: {
  work: WorkFile;
  outcome: string | null;
  onWork: (work: WorkFile) => void;
  onOutcome: (outcome: string) => void;
  onOpen: (target: ConversationLinkTarget) => void;
  /** A message to bring into view and ring ("entry-…" / "turn-…"), when the person comes back to it. */
  focus: string | null;
  /** On a phone the three lists are not beside the conversation: this opens them. */
  onLists: () => void;
}) {
  const navigate = useNavigate();
  const lastAct = settledOutcome(outcome, work.status === "WORKING");
  const [own, setOwn] = useState<Turn[]>([]);
  // Stable: the chat box calls it from an effect whenever its earlier messages change.
  const receive = useCallback((turns: Turn[]) => setOwn(turns), []);
  const items: Item[] = [
    ...work.conversation.map((entry) => ({ at: entry.at, key: entry.id, entry })),
    ...own.map((turn) => ({ at: turn.at, key: turn.turnKey, turn })),
  ].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  // Statements Leveri could not read itself: the firm's choice about outside reading is offered once, under the
  // last "I could not use it" — files sent in the chat never reached the offer on the upload form (a tester's 60
  // refused PDFs were only ever told to download CSV, 2026-10-15).
  const refusedPdfs = work.records.filter((r) => r.outcome === "REFUSED" && /\.pdf$/i.test(r.filename)).length;
  const lastRefusal = [...work.conversation].reverse().find((e) => e.id.startsWith("refused-"))?.id ?? null;
  const current = work.workpapers.find((v) => v.current) ?? work.workpapers[work.workpapers.length - 1] ?? null;

  // The newest message is where a person picks up: the conversation opens at its foot, and stays there as it grows
  // (a reply arriving, Leveri's steps) — unless the person has scrolled up to read, or came back to a message.
  const scroller = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const atFoot = useRef(true);
  const toFoot = useCallback(() => { const el = scroller.current; if (el) el.scrollTop = el.scrollHeight; }, []);
  const count = items.length;
  useEffect(() => { if (!focus) { atFoot.current = true; toFoot(); } }, [count, focus, work.status, toFoot]);
  useEffect(() => {
    const el = content.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const watch = new ResizeObserver(() => { if (atFoot.current && !focus) toFoot(); });
    watch.observe(el);
    return () => watch.disconnect();
  }, [focus, toFoot]);
  useEffect(() => {
    if (!focus) return;
    const frame = window.requestAnimationFrame(() => {
      const el = document.getElementById(focus);
      if (el) { el.scrollIntoView({ block: "center" }); el.classList.add("ring-1", "ring-forest"); window.setTimeout(() => el.classList.remove("ring-1", "ring-forest"), 1600); }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focus]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* THE WORK, STATED ONCE AND KEPT IN VIEW. */}
      <header className="shrink-0 border-b border-rule px-5 py-4 md:px-10" data-testid="work-header">
        <p className="truncate text-[11px] font-medium uppercase tracking-[0.09em] text-stone">{work.clientName} · {work.engagementTitle}</p>
        <div className="mt-1 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
          <h1 className="min-w-0 font-serif text-[24px] leading-tight text-ink [overflow-wrap:anywhere]" style={SERIF_OPSZ}>{work.title}</h1>
          {current && (
            <button type="button" onClick={() => void save(`/api/work/${work.id}/workpaper/${current.versionSeq}?format=xlsx`)} className="shrink-0 text-[13px] font-medium text-forest hover:underline" data-testid="header-workpaper-xlsx">
              Download workpaper (Excel)
            </button>
          )}
        </div>
        {/* On a phone the conversation needs the room: the period and the people are in "where it stands". */}
        <div className="mt-1.5 hidden flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-stone md:flex">
          <span>{work.periodText} · cut-off {work.cutOff} · {work.currency}</span>
          {work.startedText && <span data-testid="work-started">{work.startedText}</span>}
          <AssignControl objectKind="WORK" objectId={work.id} holder={work.assignee} onChanged={(assignee) => onWork({ ...work, assignee })} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1" data-testid="work-band">
          <Marker tone={markerTone(work.band.tone)} />
          <span className="text-[14px] font-medium text-ink">{work.band.label}</span>
          <span className="hidden text-[13px] text-stone md:inline">{work.band.note}</span>
        </div>
        <button type="button" onClick={onLists} className="mt-2 text-left text-[13px] text-ink lg:hidden" data-testid="tasks-summary">
          {`${work.tasks.asked.length} asked for · ${work.tasks.pending.length} waiting on you · ${work.tasks.done.length} done`}
          <span aria-hidden="true" className="ml-1.5 text-stone">▸</span>
        </button>
      </header>

      {/* ONLY THE CONVERSATION SCROLLS; the box stays at its foot. */}
      <div ref={scroller} onScroll={(e) => { const el = e.currentTarget; atFoot.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120; }} className="min-h-0 flex-1 overflow-y-auto px-5 md:px-10" data-testid="conversation-scroller">
        <div ref={content} className="mx-auto flex min-h-full max-w-[860px] flex-col pt-6">
          <NeedsAttention work={work} onWork={onWork} onOutcome={onOutcome} />
          <ol className="flex flex-1 flex-col gap-6" data-testid="work-conversation">
            {items.map((item) => (
              <li key={item.key} id={"entry" in item ? `entry-${item.entry.id}` : `turn-${item.turn.turnKey}`} className="scroll-mt-6">
                {"entry" in item ? <Entry entry={item.entry} onOpen={onOpen} /> : <OwnTurn turn={item.turn} onWorkOpened={(id) => navigate(`/work/${id}`)} />}
                {"entry" in item && item.entry.id === lastRefusal && refusedPdfs > 0 && <div className="ml-10 mt-2"><OutsideReadingOffer count={refusedPdfs} /></div>}
              </li>
            ))}
            {/* Before the work starts, Leveri's next line is its plan: one sentence, the line it would use, and
                "Approve and start". Everything else about starting is one tap away. */}
            {work.status === "DRAFT" && (
              <li>
                <Said>
                  <PlanLine work={work} onWork={onWork} onOutcome={onOutcome} />
                </Said>
              </li>
            )}
            {/* What the last act did — unless the conversation already says it (an answer is its own message). */}
            {lastAct && !(work.conversation.at(-1)?.text ?? "").includes(lastAct.replace(/\.$/, "").split(". ")[0]!) && (
              <li data-testid="conversation-outcome">
                <Said><p className="text-[15px] leading-relaxed text-ink">{lastAct}</p></Said>
              </li>
            )}
          </ol>
          {/* Only the box is held at the foot; Leveri's reply to the message just sent flows above it like any other. */}
          <div className="mt-6 pb-4">
            <ChatBox scope={{ workId: work.id }} placeholder="Ask about this work, or send what it is waiting for" onWork={onWork} onEarlier={receive} pinned />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Leveri's side: its mark, then what it said. */
function Said({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <LeveriMark />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function When({ who, atText, right }: { who: string | null; atText: string; right?: boolean }) {
  return <p className={`mb-1 text-[12px] text-stone ${right ? "text-right" : ""}`}>{who ? `${who} · ${atText}` : atText}</p>;
}

function Entry({ entry, onOpen }: { entry: ConversationEntry; onOpen: (target: ConversationLinkTarget) => void }) {
  if (entry.from === "NOTE") {
    return <p className="text-center text-[12px] text-stone" data-testid="conversation-note">{entry.atText} · {entry.text}</p>;
  }
  const body = (
    <>
      <p className="text-[15px] leading-relaxed text-ink">{entry.text}</p>
      {entry.lines.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {entry.lines.map((line, i) => <li key={i} className="text-[13px] leading-relaxed text-ink-soft">{line}</li>)}
        </ul>
      )}
      {entry.links.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-2" data-testid="conversation-links">
          {entry.links.map((link) => (
            <button
              key={`${link.kind}-${link.id}`}
              type="button"
              onClick={() => onOpen({ kind: link.kind, id: link.id })}
              className={`border px-3 py-1.5 text-left text-[13px] transition-colors hover:bg-band ${link.kind === "REQUEST" ? "border-att-text/40 text-ink" : "border-rule text-ink-soft"}`}
            >
              {link.kind === "REQUEST" && <span aria-hidden="true" className="mr-1.5 text-att-text">●</span>}
              {link.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
  if (entry.from === "PERSON") {
    return (
      <div className="ml-auto flex max-w-[85%] flex-col items-end" data-testid="conversation-person">
        <When who={entry.who} atText={entry.atText} right />
        <div className="bg-band px-4 py-3">{body}</div>
      </div>
    );
  }
  return (
    <div data-testid="conversation-leveri">
      <Said>
        <When who="Leveri" atText={entry.atText} />
        {body}
      </Said>
    </div>
  );
}

/** One of the person's own messages here, and Leveri's reply — private to them, set among the shared record. */
function OwnTurn({ turn, onWorkOpened }: { turn: Turn; onWorkOpened: (id: string) => void }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="ml-auto flex max-w-[85%] flex-col items-end">
        <When who="You" atText={turn.atText} right />
        <div className="bg-band px-4 py-3 text-[15px] leading-relaxed text-ink">{turn.instruction || "Files sent"}</div>
      </div>
      {(turn.reply || turn.openedWork) && (
        <Said>
          {turn.openedWork ? (
            <button type="button" onClick={() => onWorkOpened(turn.openedWork!.id)} className="text-left text-[15px] text-forest hover:underline">Opened “{turn.openedWork.title}”</button>
          ) : (
            <>
              <p className="text-[15px] leading-relaxed text-ink">{turn.reply}</p>
              {turn.lines.length > 0 && (
                <ul className="mt-1.5 space-y-1">{turn.lines.map((line, i) => <li key={i} className="text-[14px] leading-relaxed text-ink-soft">{line}</li>)}</ul>
              )}
            </>
          )}
        </Said>
      )}
    </div>
  );
}

/**
 * THE PLAN, said before it runs (the Ramp standard: propose, then approve) — now in the work, not on the home page
 * (owner, 2026-10-16: "the work should open itself"). The procedure's own summary, the line the server proposes, the
 * basis when the work offers one, and one act. No pick (nothing proposed, or records still missing): the person is told
 * what is missing and can send it in the box below, or open the full start.
 */
function PlanLine({ work, onWork, onOutcome }: { work: WorkFile; onWork: (work: WorkFile) => void; onOutcome: (outcome: string) => void }) {
  const start = work.start;
  const pick = start.canStart ? (start.candidates.find((c) => c.proposed) ?? (start.candidates.length === 1 ? start.candidates[0]! : null)) : null;
  const [basis, setBasis] = useState<"ACCRUALS" | "CASH" | null>(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const keyFor = useKeyring();

  async function approve() {
    if (!pick || busy) return;
    setBusy(true); setFailure(null);
    try {
      const result = await call("POST", `/api/work/${work.id}/start`, WorkActionResponse, {
        control: { candidateId: pick.id },
        ...(start.basisChoice && basis ? { basis } : {}),
        submissionKey: keyFor(`start:${pick.id}:${basis ?? "unchosen"}`),
      });
      onOutcome(result.outcome);
      onWork(result.work);
    } catch (error) {
      setFailure(failureMessage(error));
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3" data-testid="conversation-plan">
      <p className="text-[15px] leading-relaxed text-ink">
        {pick ? (work.procedure?.summary ?? "Here is what I will do.") : "Before I can start, I need:"}
      </p>
      {!pick && start.blockers.length > 0 && (
        <ul className="space-y-1">{start.blockers.map((b) => <li key={b} className="text-[14px] text-att-text">{b}</li>)}</ul>
      )}
      {!pick && start.blockers.length === 0 && start.missing.length > 0 && (
        <ul className="space-y-1">{start.missing.map((m) => <li key={m.role} className="text-[14px] text-att-text">{m.label}</li>)}</ul>
      )}
      {!pick && <p className="text-[13px] text-stone">Send them in the box below.</p>}
      {pick && <p className="text-[13px] text-ink-soft">{start.chooser.heading}: <span className="text-ink">{pick.label}</span></p>}
      {pick && start.basisSettled && <p className="text-[13px] text-ink-soft">{start.basisSettled}</p>}
      {pick && !start.basisSettled && start.basisChoice && (
        <div>
          <p className="text-[13px] text-ink-soft">{start.basisChoice.question}</p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {start.basisChoice.options.map((o) => (
              <button key={o.value} type="button" onClick={() => setBasis(basis === o.value ? null : o.value)} aria-pressed={basis === o.value}
                className={`border px-3 py-1.5 text-[13px] ${basis === o.value ? "border-forest text-forest" : "border-rule text-ink-soft hover:bg-band"}`}>{o.label}</button>
            ))}
          </div>
        </div>
      )}
      {work.procedure && work.procedure.steps.length > 0 && (
        <details>
          <summary className="cursor-pointer text-[13px] text-stone hover:text-ink">How I will do it</summary>
          <ol className="mt-2 space-y-1">
            {work.procedure.steps.map((step, i) => (
              <li key={step} className="grid grid-cols-[22px_minmax(0,1fr)] gap-2 text-[13px] text-ink-soft">
                <span className="text-stone [font-variant-numeric:tabular-nums]">{i + 1}</span><span>{step}</span>
              </li>
            ))}
          </ol>
          {work.procedure.boundaries.length > 0 && (
            <ul className="mt-2 space-y-1">{work.procedure.boundaries.map((b) => <li key={b} className="text-[13px] text-stone">{b}</li>)}</ul>
          )}
        </details>
      )}
      <div className="flex flex-wrap items-center gap-4 pt-1">
        {pick && (
          <button type="button" onClick={() => void approve()} disabled={busy} data-testid="plan-approve"
            className="bg-forest px-4 py-2 text-[14px] font-medium text-forest-on hover:bg-forest-hover disabled:opacity-60">
            {busy ? "Starting…" : "Approve and start"}
          </button>
        )}
        <button type="button" onClick={() => setMore(!more)} aria-expanded={more} className="text-[13px] text-stone hover:text-ink">
          {more ? "Hide" : pick ? "Change something" : "Other ways to add records"}
        </button>
      </div>
      {failure && <p className="text-[13px] text-att-text">{failure}</p>}
      {more && (
        <div className="border-t border-rule-soft pt-4">
          <StartSection workId={work.id} clientId={work.clientId} start={start} onWork={onWork} onOutcome={onOutcome} />
        </div>
      )}
    </div>
  );
}
